<script setup lang="ts">
/**
 * Input — text/password/number/email with prepend/append slots, error state.
 *
 * Spec:
 *   modelValue: string | number
 *   type?: 'text' | 'password' | 'number' | 'email'
 *   placeholder?: string
 *   error?: string       // when set, applies error styling + shows message
 *   size?: 'sm' | 'md' | 'lg'
 *   disabled?: boolean
 *   prepend?: Component  // icon component shown to the left
 *   append?: Component   // icon component shown to the right
 *
 * Slots:
 *   prepend / append     // alternative icon slots (string-free)
 */
import { computed, useAttrs } from 'vue'
import type { Component } from 'vue'
import { AlertCircle } from 'lucide-vue-next'

type InputType = 'text' | 'password' | 'number' | 'email' | 'search' | 'tel' | 'url'
type InputSize = 'sm' | 'md' | 'lg'

interface Props {
  modelValue?: string | number | null
  type?: InputType
  placeholder?: string
  error?: string
  size?: InputSize
  disabled?: boolean
  readonly?: boolean
  required?: boolean
  prepend?: Component
  append?: Component
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  size: 'md',
  disabled: false,
  readonly: false,
  required: false,
})

defineEmits<{
  'update:modelValue': [value: string]
  blur: [event: FocusEvent]
  focus: [event: FocusEvent]
}>()

const attrs = useAttrs()
const inputId = computed(() => (attrs.id as string | undefined) ?? undefined)

const sizeWrapClass = computed(() => {
  switch (props.size) {
    case 'sm': return 'h-8'
    case 'lg': return 'h-11'
    default:   return 'h-9'
  }
})

const sizeInputClass = computed(() => {
  switch (props.size) {
    case 'sm': return 'text-xs px-2'
    case 'lg': return 'text-base px-4'
    default:   return 'text-sm px-3'
  }
})

const wrapperClass = computed(() => {
  const base = 'flex w-full items-center rounded-md border bg-bg-surface transition-colors duration-fast focus-within:ring-2 focus-within:ring-ring'
  if (props.error) {
    return `${base} border-danger focus-within:border-danger`
  }
  return `${base} border-input focus-within:border-primary-500`
})
</script>

<template>
  <div class="w-full">
    <div
      :class="[
        wrapperClass,
        sizeWrapClass,
        disabled ? 'opacity-50 cursor-not-allowed' : '',
      ]"
    >
      <!-- prepend slot / icon -->
      <span
        v-if="$slots.prepend || prepend"
        class="flex shrink-0 items-center pl-2 text-fg-muted"
        :class="size === 'sm' ? 'pl-1.5' : size === 'lg' ? 'pl-3' : 'pl-2'"
      >
        <slot name="prepend">
          <component :is="prepend" :size="size === 'lg' ? 18 : 14" />
        </slot>
      </span>

      <input
        v-bind="$attrs"
        :id="inputId"
        :value="modelValue ?? ''"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="error ? `${inputId ?? 'input'}-error` : undefined"
        :class="[
          'flex-1 min-w-0 bg-transparent text-foreground placeholder:text-muted-foreground outline-none border-0',
          sizeInputClass,
          ($slots.prepend || prepend) ? 'pl-1' : '',
          ($slots.append || append) ? 'pr-1' : '',
        ]"
        @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      />

      <!-- append slot / icon -->
      <span
        v-if="$slots.append || append"
        class="flex shrink-0 items-center pr-2 text-fg-muted"
        :class="size === 'sm' ? 'pr-1.5' : size === 'lg' ? 'pr-3' : 'pr-2'"
      >
        <slot name="append">
          <component :is="append" :size="size === 'lg' ? 18 : 14" />
        </slot>
      </span>
    </div>

    <!-- error message -->
    <p
      v-if="error"
      :id="`${inputId ?? 'input'}-error`"
      class="mt-1 flex items-center gap-1 text-xs text-danger"
      role="alert"
    >
      <AlertCircle :size="12" />
      <span>{{ error }}</span>
    </p>
  </div>
</template>
